/**
 * The scoreboard, as seen on the READY and FINISH overlays.
 *
 * Shows one category at a time - a 0.00 promille run and a 3.50 promille run
 * are not the same game, so they are not the same table.
 *
 * The "V" badge on a row means the server holds a replay for it and has
 * re-simulated it. That is the honest claim: not "we trust this player" but
 * "this time was computed from inputs anyone can download and re-check".
 */

import { parseRunCategory } from '../score/runCategory';
import { describeIntoxication } from '../config/intoxication';
import { formatTime } from '../utils/format';
import type { LeaderboardEntry } from '../score/Leaderboard';
import { button, el, setHidden, setText } from './dom';

export type BoardStatus = 'loading' | 'ready' | 'offline' | 'empty' | 'error';

export class LeaderboardPanel {
  readonly root = el('div', 'board');
  onSelect: (entry: LeaderboardEntry) => void = () => {};
  onButtonsChanged: (buttons: HTMLButtonElement[]) => void = () => {};
  private rowButtons: HTMLButtonElement[] = [];

  private readonly heading = el('div', 'board__heading', 'TULOSTAULU');
  private readonly subheading = el('div', 'board__sub');
  private readonly status = el('div', 'board__status', 'Ladataan...');
  private readonly rows = el('ol', 'board__rows');
  /** The run id to highlight, so a player can find themselves in the list. */
  private highlightRunId: string | null = null;

  constructor(title = 'TULOSTAULU', private readonly view: 'full' | 'top10' = 'full') {
    setText(this.heading, title);
    this.root.append(this.heading);
    if (view === 'full') this.root.append(this.subheading);
    this.root.append(this.status, this.rows);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.root);
  }

  setVisible(visible: boolean): void {
    setHidden(this.root, !visible);
  }

  highlight(runId: string | null): void {
    this.highlightRunId = runId;
  }

  setStatus(status: BoardStatus, detail?: string): void {
    const message: Record<BoardStatus, string> = {
      loading: 'Ladataan...',
      ready: '',
      offline: 'Ei yhteyttä tulostauluun - tulokset jäävät tälle koneelle.',
      empty: 'Ei vielä tuloksia tällä seedillä ja annoksella.',
      error: detail ?? 'Tulostaulua ei saatu haettua.',
    };
    setText(this.status, message[status]);
    setHidden(this.status, status === 'ready');
    setHidden(this.rows, status !== 'ready');
    this.onButtonsChanged(status === 'ready' ? this.rowButtons : []);
  }

  render(entries: readonly LeaderboardEntry[], seedLabel?: string): void {
    if (entries.length > 0) {
      const identity = parseRunCategory(entries[0].category);
      setText(this.subheading, `${describeIntoxication(entries[0].intoxication)}${identity ? ` · Seed ${seedLabel ?? identity.seed}` : ''}`);
    }

    this.rows.replaceChildren();
    this.rowButtons = [];
    const visibleEntries = this.view === 'top10' ? entries.slice(0, 10) : entries;
    for (const entry of visibleEntries) {
      const item = el('li');
      const row = button('', () => {
        row.focus();
        this.onSelect(entry);
      }, 'board__row');
      row.replaceChildren();
      row.setAttribute('aria-label', `${entry.rank}. ${entry.name}, ${formatTime(entry.timeMs)}. Näytä väliajat`);
      row.setAttribute('aria-haspopup', 'dialog');
      row.title = 'Näytä kaikki checkpoint-ajat';
      if (entry.runId && entry.runId === this.highlightRunId) {
        row.classList.add('board__row--mine');
      }

      row.append(
        el('span', 'board__rank', `${entry.rank}.`),
        el('span', 'board__name', entry.name),
        el('span', 'board__time', formatTime(entry.timeMs)),
      );

      if (this.view === 'full') {
        const badge = el('span', 'board__verified', entry.hasReplay ? 'V' : '');
        badge.title = entry.hasReplay
          ? 'Replay tallessa - aika on laskettu uudelleen syötteistä.'
          : '';
        row.appendChild(badge);
      }

      item.append(row);
      this.rows.appendChild(item);
      this.rowButtons.push(row);
    }

    this.setStatus(entries.length === 0 ? 'empty' : 'ready');
  }

  /** Used before a category's rows have arrived, so the panel does not flicker. */
  setCategoryLabel(label: string): void {
    setText(this.subheading, label);
  }
}
